/**
 * ユーザー管理共通ライブラリ
 * ユーザー登録、ユーザー更新、ユーザー削除、ユーザー情報取得
 * 
 */

const { info, error: logError } = require('./logging');
const { handleApiError } = require('./error-handling');


/**
 * API Gateway URL
 * ログイン要求APIのエンドポイントURL
 */
// ビルド時に GitHub Secrets から注入される想定。
// ビルド時に process.env.API_GATEWAY_URL が文字列リテラルに置き換えられる
const API_GATEWAY_URL = process.env.API_GATEWAY_URL || '';
if (!API_GATEWAY_URL) {
  throw new Error('API_GATEWAY_URL is not defined. Set it via build-time environment (e.g., GitHub Secrets).');
}

/**
 * ユーザー情報取得
 * ユーザー情報取得APIを呼び出す
 * 
 * 設計ドキュメント: 06_08_ユーザー情報取得処理詳細設計.md
 * 
 * @param {number} [originalUserId] - 取得対象のユーザーID（任意、指定しない場合はJWTからoriginalUserIdを取得して本人の情報を取得）
 * @returns {Promise<UserInfo>} ユーザー情報
 * @throws {Error} バリデーションエラーまたはAPI呼び出しエラー
 * 
 * @typedef {Object} UserInfo
 * @property {string} userId - Auth0のユーザーID
 * @property {number} originalUserId - カスタムDBのユーザーID
 * @property {string} email - メールアドレス
 * @property {string} name - ユーザー名
 * @property {number} userKbn - ユーザー区分
 * @property {Role[]} [roles] - ロール情報（任意）
 * @property {Hall[]} [halls] - ホール情報（任意）
 * @property {string} [createdAt] - 登録日時（任意）
 * @property {string} [updatedAt] - 更新日時（任意）
 * @property {Object} [metadata] - メタデータ（任意）
 * 
 * @typedef {Object} Role
 * @property {string} roleId - ロールID
 * @property {string} roleName - ロール名
 * 
 * @typedef {Object} Hall
 * @property {string} hallId - ホールID
 * @property {string} hallName - ホール名
 * @property {string} region - 地域
 * @property {string} address - 住所
 * @property {string} [phone] - 電話番号（任意）
 */
async function getUserInfo(originalUserId) {
  // ブラウザ環境チェック
  if (typeof window === 'undefined') {
    const error = new Error('E005-0003: ブラウザ環境で実行する必要があります');
    error.code = 'E005-0003';
    error.statusCode = 500;
    
    logError('ユーザー情報取得処理で環境エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'GET',
        path: '/api/users',
        status: 500
      },
      data: {
        message_id: 'E005-0003'
      }
    });
    
    throw error;
  }

  // API Gateway URLの取得
  const apiGatewayUrl = API_GATEWAY_URL;
  if (!apiGatewayUrl || typeof apiGatewayUrl !== 'string' || apiGatewayUrl.trim().length === 0) {
    const error = new Error('E005-0003: 環境変数が設定されていない');
    error.code = 'E005-0003';
    error.statusCode = 500;
    
    logError('ユーザー情報取得処理で環境変数エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'GET',
        path: '/api/users',
        status: 500
      },
      data: {
        message_id: 'E005-0003'
      }
    });
    
    throw error;
  }

  // 1. ユーザー情報取得API（GET /api/users）を呼び出し
  // 注意: HttpOnly Cookieはブラウザが自動的に送信するため、Authorizationヘッダーは不要
  // 注意: Cookieが自動的に送信される（`access_token`を含むHttpOnly Cookie）
  // originalUserIdが指定されている場合はクエリパラメータとして送信
  // 指定されていない場合は、JWTからoriginalUserIdを取得して使用（API側で処理）
  const apiUrl = new URL(`${apiGatewayUrl}/api/users`);
  if (originalUserId !== undefined && originalUserId !== null) {
    // originalUserIdのバリデーション
    const originalUserIdNum = typeof originalUserId === 'number' 
      ? originalUserId 
      : parseInt(originalUserId, 10);
    
    if (isNaN(originalUserIdNum) || !Number.isInteger(originalUserIdNum) || originalUserIdNum < 1) {
      const error = new Error('E004-0001: パラメータの形式が正しくありません（originalUserId）');
      error.code = 'E004-0001';
      error.statusCode = 400;
      
      logError('ユーザー情報取得処理でパラメータバリデーションエラーが発生しました', {
        event: 'error',
        category: 'validation',
        http: {
          method: 'GET',
          path: '/api/users',
          status: 400
        },
        data: {
          message_id: 'E004-0001',
          originalUserId: originalUserId
        }
      });
      
      throw error;
    }
    
    apiUrl.searchParams.append('originalUserId', String(originalUserIdNum));
  }

  let response;
  try {
    response = await fetch(apiUrl.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include' // Cookieを送信するために必要
    });
  } catch (fetchError) {
    // fetch自体がエラーを投げた場合（ネットワークエラー）
    const networkError = new Error('E006-0004: ネットワークエラーが発生しました');
    networkError.code = 'E006-0004';
    networkError.statusCode = 504;
    networkError.originalError = fetchError;
    
    logError('ユーザー情報取得処理でネットワークエラーが発生しました', {
      event: 'error',
      category: 'network',
      http: {
        method: 'GET',
        path: '/api/users',
        status: 504
      },
      data: {
        message_id: 'E006-0004'
      }
    });
    
    throw networkError;
  }

  // レスポンスの解析
  let responseData;
  try {
    responseData = await response.json();
  } catch (parseError) {
    const error = new Error('E005-0004: ユーザー情報取得APIの取得エラー等');
    error.code = 'E005-0004';
    error.statusCode = 500;
    error.originalError = parseError;
    
    logError('ユーザー情報取得処理でレスポンス解析エラーが発生しました', {
      event: 'error',
      category: 'api',
      http: {
        method: 'GET',
        path: '/api/users',
        status: 500
      },
      data: {
        message_id: 'E005-0004'
      }
    });
    
    throw error;
  }

  // エラーレスポンスの処理
  if (!response.ok) {
    const apiError = handleApiError(response, responseData, 'ユーザー情報取得APIの取得エラー等');
    
    // エラーログ出力
    logError('ユーザー情報取得処理でAPIエラーが発生しました', {
      event: 'error',
      category: 'api',
      http: {
        method: 'GET',
        path: '/api/users',
        status: apiError.statusCode
      },
      data: {
        message_id: apiError.code || 'E005-0004'
      }
    });
    
    throw apiError;
  }

  // 2. レスポンスからdataを取得
  if (!responseData.data) {
    const error = new Error('E005-0004: ユーザー情報取得APIの取得エラー等');
    error.code = 'E005-0004';
    error.statusCode = 500;
    
    logError('ユーザー情報取得処理でレスポンスデータが不正です', {
      event: 'error',
      category: 'api',
      http: {
        method: 'GET',
        path: '/api/users',
        status: 500
      },
      data: {
        message_id: 'E005-0004'
      }
    });
    
    throw error;
  }

  const userData = responseData.data;

  // 3. ユーザー情報を返却
  // アクセスログ出力
  info('ユーザー情報取得処理が正常に完了しました', {
    event: 'user_info_success',
    category: 'user',
    http: {
      method: 'GET',
      path: '/api/users',
      status: 200
    },
    data: {
      originalUserId: userData.originalUserId || null,
      has_roles: userData.roles && userData.roles.length > 0,
      has_halls: userData.halls && userData.halls.length > 0
    }
  });

  return userData;
}

/**
 * メールアドレスのバリデーション
 * @param {string} email - 検証するメールアドレス
 * @returns {Object} バリデーション結果
 */
function validateEmail(email) {
  if (!email || typeof email !== 'string' || email.trim().length === 0) {
    return {
      isValid: false,
      errorCode: 'E004-0002',
      errorMessage: '必須パラメータが不足しています（email）',
      statusCode: 400
    };
  }
  
  // email形式のチェック
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(email)) {
    return {
      isValid: false,
      errorCode: 'E004-0001',
      errorMessage: 'パラメータが不正です（email）',
      statusCode: 400
    };
  }
  
  // 256文字以下のチェック
  if (email.length > 256) {
    return {
      isValid: false,
      errorCode: 'E004-0001',
      errorMessage: 'パラメータが不正です（email）',
      statusCode: 400
    };
  }
  
  return { isValid: true };
}

/**
 * パスワード強度のバリデーション（777ECのパスワードポリシーに準拠）
 * @param {string} password - 検証するパスワード
 * @returns {Object} バリデーション結果
 */
function validatePasswordStrength(password) {
  if (!password || typeof password !== 'string' || password.trim().length === 0) {
    return {
      isValid: false,
      errorCode: 'E004-0002',
      errorMessage: '必須パラメータが不足しています（password）',
      statusCode: 400
    };
  }
  
  // 777ECのパスワードポリシー
  // - 最小8文字以上
  // - 大文字（A-Z）を含む
  // - 小文字（a-z）を含む
  // - 数字（0-9）を含む
  if (password.length < 8) {
    return {
      isValid: false,
      errorCode: 'E004-0004',
      errorMessage: '正しいパスワード形式で入力してください（password）',
      statusCode: 400
    };
  }
  
  if (!/[A-Z]/.test(password)) {
    return {
      isValid: false,
      errorCode: 'E004-0004',
      errorMessage: '正しいパスワード形式で入力してください（password）',
      statusCode: 400
    };
  }
  
  if (!/[a-z]/.test(password)) {
    return {
      isValid: false,
      errorCode: 'E004-0004',
      errorMessage: '正しいパスワード形式で入力してください（password）',
      statusCode: 400
    };
  }
  
  if (!/[0-9]/.test(password)) {
    return {
      isValid: false,
      errorCode: 'E004-0004',
      errorMessage: '正しいパスワード形式で入力してください（password）',
      statusCode: 400
    };
  }
  
  return { isValid: true };
}

/**
 * ユーザー名のバリデーション
 * @param {string} name - 検証するユーザー名
 * @returns {Object} バリデーション結果
 */
function validateName(name) {
  if (!name || typeof name !== 'string' || name.trim().length === 0) {
    return {
      isValid: false,
      errorCode: 'E004-0002',
      errorMessage: '必須パラメータが不足しています（name）',
      statusCode: 400
    };
  }
  
  // 300桁以下のチェック
  if (name.length > 300) {
    return {
      isValid: false,
      errorCode: 'E004-0001',
      errorMessage: 'パラメータが不正です（name）',
      statusCode: 400
    };
  }
  
  return { isValid: true };
}

/**
 * ユーザー区分のバリデーション
 * @param {number} userKbn - 検証するユーザー区分
 * @returns {Object} バリデーション結果
 */
function validateUserKbn(userKbn) {
  if (userKbn === undefined || userKbn === null) {
    return {
      isValid: false,
      errorCode: 'E004-0002',
      errorMessage: '必須パラメータが不足しています（userKbn）',
      statusCode: 400
    };
  }
  
  // 有効なユーザー区分: 1, 2, 3, 4, 5, 6, 7, 9
  const validUserKbns = [1, 2, 3, 4, 5, 6, 7, 9];
  if (!validUserKbns.includes(Number(userKbn))) {
    return {
      isValid: false,
      errorCode: 'E004-0005',
      errorMessage: '正しいユーザー区分で入力してください（userKbn）',
      statusCode: 400
    };
  }
  
  return { isValid: true };
}

/**
 * halls配列のバリデーション
 * @param {any} halls - 検証するhalls（配列または単一値）
 * @param {number} userKbn - ユーザー区分
 * @returns {Object} バリデーション結果と正規化された配列
 */
function validateHalls(halls, userKbn) {
  // ユーザー区分が1:ホールまたは2:法人の場合、hallsは必須
  if (userKbn === 1 || userKbn === 2) {
    if (halls === undefined || halls === null) {
      return {
        isValid: false,
        errorCode: 'E004-0002',
        errorMessage: '必須パラメータが不足しています（halls）',
        statusCode: 400,
        normalizedArray: null
      };
    }
    
    // 非配列の場合は配列に変換
    let hallsArray = Array.isArray(halls) ? halls : [halls];
    
    if (hallsArray.length === 0) {
      return {
        isValid: false,
        errorCode: 'E004-0002',
        errorMessage: '必須パラメータが不足しています（halls）',
        statusCode: 400,
        normalizedArray: null
      };
    }
    
    // 各要素のバリデーション
    for (const item of hallsArray) {
      if (typeof item !== 'string' || item.trim().length === 0) {
        return {
          isValid: false,
          errorCode: 'E004-0001',
          errorMessage: 'パラメータが不正です（halls）',
          statusCode: 400,
          normalizedArray: null
        };
      }
    }
    
    return {
      isValid: true,
      normalizedArray: hallsArray
    };
  } else {
    // ユーザー区分が1または2以外の場合、hallsが設定されている場合は配列に変換
    if (halls !== undefined && halls !== null) {
      const hallsArray = Array.isArray(halls) ? halls : [halls];
      return {
        isValid: true,
        normalizedArray: hallsArray
      };
    }
    
    return {
      isValid: true,
      normalizedArray: null
    };
  }
}

/**
 * transport_codeのバリデーション
 * @param {string} transportCode - 検証する配送会社コード
 * @param {number} userKbn - ユーザー区分
 * @returns {Object} バリデーション結果
 */
function validateTransportCode(transportCode, userKbn) {
  // ユーザー区分が9:配送会社の場合、transport_codeは必須
  if (userKbn === 9) {
    if (transportCode === undefined || transportCode === null) {
      return {
        isValid: false,
        errorCode: 'E004-0002',
        errorMessage: '必須パラメータが不足しています（transport_code）',
        statusCode: 400
      };
    }
    
    if (typeof transportCode !== 'string' || transportCode.trim().length === 0) {
      return {
        isValid: false,
        errorCode: 'E004-0001',
        errorMessage: 'パラメータが不正です（transport_code）',
        statusCode: 400
      };
    }
    
    return { isValid: true };
  } else {
    // ユーザー区分が9以外の場合、transport_codeが設定されている場合は形式チェック
    if (transportCode !== undefined && transportCode !== null) {
      if (typeof transportCode !== 'string' || transportCode.trim().length === 0) {
        return {
          isValid: false,
          errorCode: 'E004-0001',
          errorMessage: 'パラメータが不正です（transport_code）',
          statusCode: 400
        };
      }
    }
    
    return { isValid: true };
  }
}

/**
 * ブール値のバリデーション
 * @param {any} value - 検証する値
 * @returns {boolean} 有効なブール値かどうか
 * 
 * 注意: API側のvalidateBooleanはboolean型のみを許可するが、
 * クライアント側では0/1や'0'/'1'も許可する（後方互換性のため）
 */
function isValidBoolean(value) {
  // null/undefinedは任意パラメータとして許可
  if (value === null || value === undefined) {
    return true;
  }
  // boolean型、または数値/文字列の0/1を許可
  return typeof value === 'boolean' || value === 0 || value === 1 || value === '0' || value === '1';
}

/**
 * ユーザー登録
 * ユーザー登録API（POST /api/users）を呼び出す
 * 
 * 設計ドキュメント: 06_13_ユーザー登録処理詳細設計.md
 * 
 * @param {Object} userData - ユーザー登録情報
 * @param {string} userData.email - メールアドレス（必須）
 * @param {string} userData.password - パスワード（必須）
 * @param {string} userData.name - ユーザー名（必須）
 * @param {number} userData.userKbn - ユーザー区分（必須）
 * @param {number[]} [userData.roles] - ロール配列（任意）
 * @param {string} [userData.mail02] - サブメールアドレス（任意）
 * @param {string} [userData.sys_client_code] - 基幹取引先コード（任意）
 * @param {boolean} [userData.transport_manage_flg] - 配送管理責任者フラグ（任意）
 * @param {boolean} [userData.manage_flg] - 管理責任者フラグ（任意）
 * @param {string[]|string} [userData.halls] - ホール（法人）コード配列（条件付き必須）
 * @param {string} [userData.transport_code] - 配送会社コード（条件付き必須）
 * @param {Object} [userData.mail_setting] - 受信メール設定（任意）
 * @returns {Promise<UserInfo>} 登録されたユーザー情報
 * @throws {Error} バリデーションエラーまたはAPI呼び出しエラー
 * 
 * @typedef {Object} UserInfo
 * @property {number} userId - ユーザーID（自動採番）
 * @property {string} email - メールアドレス
 * @property {string} name - ユーザー名
 * @property {number} userKbn - ユーザー区分
 * @property {number[]} [roles] - ロール配列（任意）
 * @property {string} createdAt - 登録日時
 */
async function registerUser(userData) {
  // ブラウザ環境チェック
  if (typeof window === 'undefined') {
    const error = new Error('E005-0003: ブラウザ環境で実行する必要があります');
    error.code = 'E005-0003';
    error.statusCode = 500;
    
    logError('ユーザー登録処理で環境エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'POST',
        path: '/api/users',
        status: 500
      },
      data: {
        message_id: 'E005-0003'
      }
    });
    
    throw error;
  }

  // API Gateway URLの取得
  const apiGatewayUrl = API_GATEWAY_URL;
  if (!apiGatewayUrl || typeof apiGatewayUrl !== 'string' || apiGatewayUrl.trim().length === 0) {
    const error = new Error('E005-0003: 環境変数が設定されていない');
    error.code = 'E005-0003';
    error.statusCode = 500;
    
    logError('ユーザー登録処理で環境変数エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'POST',
        path: '/api/users',
        status: 500
      },
      data: {
        message_id: 'E005-0003'
      }
    });
    
    throw error;
  }

  // 1. パラメータのバリデーション
  const validationErrors = [];
  
  // emailのバリデーション
  const emailValidation = validateEmail(userData.email);
  if (!emailValidation.isValid) {
    validationErrors.push({
      field: 'email',
      errorCode: emailValidation.errorCode,
      errorMessage: emailValidation.errorMessage
    });
  }
  
  // passwordのバリデーション
  const passwordValidation = validatePasswordStrength(userData.password);
  if (!passwordValidation.isValid) {
    validationErrors.push({
      field: 'password',
      errorCode: passwordValidation.errorCode,
      errorMessage: passwordValidation.errorMessage
    });
  }
  
  // nameのバリデーション
  const nameValidation = validateName(userData.name);
  if (!nameValidation.isValid) {
    validationErrors.push({
      field: 'name',
      errorCode: nameValidation.errorCode,
      errorMessage: nameValidation.errorMessage
    });
  }
  
  // userKbnのバリデーション（clientとtransport_codeのバリデーションより先に実行）
  const userKbnValidation = validateUserKbn(userData.userKbn);
  if (!userKbnValidation.isValid) {
    validationErrors.push({
      field: 'userKbn',
      errorCode: userKbnValidation.errorCode,
      errorMessage: userKbnValidation.errorMessage
    });
  }
  
  
  // mail02のバリデーション（設定されている場合のみ）
  if (userData.mail02 !== undefined && userData.mail02 !== null) {
    const mail02Validation = validateEmail(userData.mail02);
    if (!mail02Validation.isValid) {
      validationErrors.push({
        field: 'mail02',
        errorCode: mail02Validation.errorCode,
        errorMessage: mail02Validation.errorMessage
      });
    }
  }
  
  // hallsのバリデーション（userKbnが有効な場合のみ実行）
  let hallsValidation = { isValid: true, normalizedArray: null };
  if (userKbnValidation.isValid) {
    hallsValidation = validateHalls(userData.halls, userData.userKbn);
    if (!hallsValidation.isValid) {
      validationErrors.push({
        field: 'halls',
        errorCode: hallsValidation.errorCode,
        errorMessage: hallsValidation.errorMessage
      });
    }
  }
  
  // transport_codeのバリデーション（userKbnが有効な場合のみ実行）
  let transportCodeValidation = { isValid: true };
  if (userKbnValidation.isValid) {
    transportCodeValidation = validateTransportCode(userData.transport_code, userData.userKbn);
    if (!transportCodeValidation.isValid) {
      validationErrors.push({
        field: 'transport_code',
        errorCode: transportCodeValidation.errorCode,
        errorMessage: transportCodeValidation.errorMessage
      });
    }
  }
  
  // transport_manage_flgのバリデーション（設定されている場合のみ）
  if (userData.transport_manage_flg !== undefined && userData.transport_manage_flg !== null) {
    if (!isValidBoolean(userData.transport_manage_flg)) {
      validationErrors.push({
        field: 'transport_manage_flg',
        errorCode: 'E004-0001',
        errorMessage: 'パラメータが不正です（transport_manage_flg）'
      });
    }
  }
  
  // manage_flgのバリデーション（設定されている場合のみ）
  if (userData.manage_flg !== undefined && userData.manage_flg !== null) {
    if (!isValidBoolean(userData.manage_flg)) {
      validationErrors.push({
        field: 'manage_flg',
        errorCode: 'E004-0001',
        errorMessage: 'パラメータが不正です（manage_flg）'
      });
    }
  }
  
  // バリデーションエラーがある場合はエラーをthrow
  if (validationErrors.length > 0) {
    const firstError = validationErrors[0];
    const error = new Error(`${firstError.errorCode}: ${firstError.errorMessage}`);
    error.code = firstError.errorCode;
    error.statusCode = 400; // バリデーションエラーはすべて400
    error.details = validationErrors;
    
    logError('ユーザー登録処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      http: {
        method: 'POST',
        path: '/api/users',
        status: error.statusCode
      },
      data: {
        message_id: firstError.errorCode,
        validation_errors: validationErrors
      }
    });
    
    throw error;
  }
  
  // リクエストボディの準備
  const requestBody = {
    email: userData.email,
    password: userData.password,
    name: userData.name,
    userKbn: userData.userKbn
  };
  
  // オプションパラメータの追加
  if (userData.roles !== undefined && userData.roles !== null) {
    requestBody.roles = userData.roles;
  }
  if (userData.mail02 !== undefined && userData.mail02 !== null) {
    requestBody.mail02 = userData.mail02;
  }
  if (userData.sys_client_code !== undefined && userData.sys_client_code !== null) {
    requestBody.sys_client_code = userData.sys_client_code;
  }
  if (userData.transport_manage_flg !== undefined && userData.transport_manage_flg !== null) {
    requestBody.transport_manage_flg = userData.transport_manage_flg;
  }
  if (userData.manage_flg !== undefined && userData.manage_flg !== null) {
    requestBody.manage_flg = userData.manage_flg;
  }
  if (hallsValidation.normalizedArray !== null) {
    requestBody.halls = hallsValidation.normalizedArray;
  }
  if (userData.transport_code !== undefined && userData.transport_code !== null) {
    requestBody.transport_code = userData.transport_code;
  }
  if (userData.mail_setting !== undefined && userData.mail_setting !== null) {
    requestBody.mail_setting = userData.mail_setting;
  }
  
  // 2. ユーザー登録API（POST /api/users）を呼び出し（リトライ処理付き）
  const apiUrl = `${apiGatewayUrl}/api/users`;
  const maxRetries = 3;
  let lastError = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    let response;
    try {
      response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // HttpOnly Cookieを送信するために必要
        body: JSON.stringify(requestBody)
      });
    } catch (fetchError) {
      // ネットワークエラーの場合、リトライ可能
      lastError = fetchError;
      
      if (attempt < maxRetries) {
        // リトライ前に待機（指数バックオフ）
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
        continue;
      }
      
      // 最大リトライ回数に達した場合
      const networkError = new Error('E006-0004: ネットワークエラーが発生しました');
      networkError.code = 'E006-0004';
      networkError.statusCode = 504;
      networkError.originalError = fetchError;
      
      logError('ユーザー登録処理でネットワークエラーが発生しました', {
        event: 'error',
        category: 'network',
        http: {
          method: 'POST',
          path: '/api/users',
          status: 504
        },
        data: {
          message_id: 'E006-0004',
          retry_attempt: attempt
        }
      });
      
      throw networkError;
    }
    
    // レスポンスの解析
    let responseData;
    try {
      responseData = await response.json();
    } catch (parseError) {
      // レスポンス解析エラーはリトライ対象外（JSON形式が不正な場合はリトライしても解決しない）
      const error = new Error('E005-0004: ユーザー登録APIの取得エラー等');
      error.code = 'E005-0004';
      error.statusCode = 500;
      error.originalError = parseError;
      
      logError('ユーザー登録処理でレスポンス解析エラーが発生しました', {
        event: 'error',
        category: 'api',
        http: {
          method: 'POST',
          path: '/api/users',
          status: 500
        },
        data: {
          message_id: 'E005-0004'
        }
      });
      
      throw error;
    }
    
    // エラーレスポンスの処理
    if (!response.ok) {
      // 5xxエラー（サーバーエラー）はリトライ可能（429レート制限エラーを除く）
      const isRetryableError = response.status >= 500 && response.status !== 429;
      
      if (isRetryableError && attempt < maxRetries) {
        // リトライ前に待機（指数バックオフ）
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
        continue;
      }
      
      const apiError = handleApiError(response, responseData, 'ユーザー登録APIの取得エラー等');
      
      // エラーログ出力
      logError('ユーザー登録処理でAPIエラーが発生しました', {
        event: 'error',
        category: 'api',
        http: {
          method: 'POST',
          path: '/api/users',
          status: apiError.statusCode
        },
        data: {
          message_id: apiError.code || 'E005-0004',
          retry_attempt: attempt
        }
      });
      
      throw apiError;
    }
    
    // 3. レスポンスからdataを取得
    if (!responseData.data) {
      const error = new Error('E005-0004: ユーザー登録APIの取得エラー等');
      error.code = 'E005-0004';
      error.statusCode = 500;
      
      logError('ユーザー登録処理でレスポンスデータが不正です', {
        event: 'error',
        category: 'api',
        http: {
          method: 'POST',
          path: '/api/users',
          status: 500
        },
        data: {
          message_id: 'E005-0004'
        }
      });
      
      throw error;
    }
    
    const userInfo = responseData.data;
    
    // 4. 登録結果を返却
    // アクセスログ出力
    info('ユーザー登録処理が正常に完了しました', {
      event: 'user_registration_success',
      category: 'user',
      http: {
        method: 'POST',
        path: '/api/users',
        status: 200
      },
      data: {
        userId: userInfo.userId,
        email: userInfo.email,
        userKbn: userInfo.userKbn
      }
    });
    
    return userInfo;
  }
  
  // ここには到達しないはずだが、念のため
  throw lastError || new Error('E000-0001: システムエラーが発生しました');
}

/**
 * ユーザー更新
 * ユーザー更新API（PUT /api/users/{originalUserId}）を呼び出す
 * 
 * 設計ドキュメント: 06_15_ユーザー更新処理詳細設計.md
 * 
 * @param {number} originalUserId - カスタムDBのユーザーID（必須）
 * @param {Object} userData - ユーザー更新情報（必須）
 * @param {string} [userData.password] - パスワード（平文）（任意、passwordが設定されている場合のみ更新される）
 * @param {string} [userData.name] - ユーザー名（任意）
 * @param {number} [userData.userKbn] - ユーザー区分（任意）
 * @param {number[]} [userData.roles] - ロール配列（任意）
 * @param {boolean} [userData.manage_flg] - 管理責任者フラグ（任意）
 * @param {string} [userData.mail02] - サブメールアドレス（任意）
 * @param {string} [userData.sys_client_code] - 基幹取引先コード（任意）
 * @param {boolean} [userData.transport_manage_flg] - 配送管理責任者フラグ（任意）
 * @param {string[]|string} [userData.halls] - ホール（法人）コード配列（条件付き必須）
 * @param {string} [userData.transport_code] - 配送会社コード（条件付き必須）
 * @param {Object} [userData.mail_setting] - 受信メール設定（任意）
 * @param {string[]} [userData.payer] - 支払人データ配列（任意）
 * @returns {Promise<UserInfo>} 更新されたユーザー情報
 * @throws {Error} バリデーションエラーまたはAPI呼び出しエラー
 * 
 * @typedef {Object} UserInfo
 * @property {number} originalUserId - 777EC DBのユーザーID
 * @property {string} name - ユーザー名
 * @property {number} userKbn - ユーザー区分
 * @property {number[]} [roles] - ロール配列（任意）
 * @property {string} updatedAt - 更新日時
 * @property {boolean} [manage_flg] - 管理責任者フラグ（設定されている場合のみ）
 * @property {string} [mail02] - サブメールアドレス（設定されている場合のみ）
 * @property {string} [sys_client_code] - 基幹取引先コード（設定されている場合のみ）
 * @property {boolean} [transport_manage_flg] - 配送管理責任者フラグ（設定されている場合のみ）
 * @property {string[]} [halls] - ホール（法人）コード配列（設定されている場合のみ）
 * @property {string} [transport_code] - 配送会社コード（設定されている場合のみ）
 * @property {Object} [mail_setting] - 受信メール設定（設定されている場合のみ）
 * @property {string[]} [payer] - 支払人データ配列（設定されている場合のみ）
 */
async function updateUser(originalUserId, userData) {
  // ブラウザ環境チェック
  if (typeof window === 'undefined') {
    const error = new Error('E005-0003: ブラウザ環境で実行する必要があります');
    error.code = 'E005-0003';
    error.statusCode = 500;
    
    logError('ユーザー更新処理で環境エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'PUT',
        path: `/api/users/${originalUserId}`,
        status: 500
      },
      data: {
        message_id: 'E005-0003'
      }
    });
    
    throw error;
  }

  // API Gateway URLの取得
  const apiGatewayUrl = API_GATEWAY_URL;
  if (!apiGatewayUrl || typeof apiGatewayUrl !== 'string' || apiGatewayUrl.trim().length === 0) {
    const error = new Error('E005-0003: 環境変数が設定されていない');
    error.code = 'E005-0003';
    error.statusCode = 500;
    
    logError('ユーザー更新処理で環境変数エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'PUT',
        path: `/api/users/${originalUserId}`,
        status: 500
      },
      data: {
        message_id: 'E005-0003'
      }
    });
    
    throw error;
  }

  // 1. originalUserIdのバリデーション
  if (originalUserId === undefined || originalUserId === null) {
    const error = new Error('E004-0002: 必須パラメータが不足しています（originalUserId）');
    error.code = 'E004-0002';
    error.statusCode = 400;
    
    logError('ユーザー更新処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      http: {
        method: 'PUT',
        path: `/api/users/${originalUserId}`,
        status: 400
      },
      data: {
        message_id: 'E004-0002',
        field: 'originalUserId'
      }
    });
    
    throw error;
  }

  const originalUserIdNum = typeof originalUserId === 'number' 
    ? originalUserId 
    : parseInt(originalUserId, 10);
  
  if (isNaN(originalUserIdNum) || originalUserIdNum < 0) {
    const error = new Error('E004-0001: パラメータが不正です（originalUserId）');
    error.code = 'E004-0001';
    error.statusCode = 400;
    
    logError('ユーザー更新処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      http: {
        method: 'PUT',
        path: `/api/users/${originalUserId}`,
        status: 400
      },
      data: {
        message_id: 'E004-0001',
        field: 'originalUserId'
      }
    });
    
    throw error;
  }

  // 2. パラメータのバリデーション
  const validationErrors = [];
  
  // passwordのバリデーション（passwordが設定されている場合のみパスワード更新）
  if (userData.password !== undefined && userData.password !== null) {
    if (typeof userData.password !== 'string' || userData.password.trim().length === 0) {
      validationErrors.push({
        field: 'password',
        errorCode: 'E004-0002',
        errorMessage: '必須パラメータが不足しています（password）'
      });
    } else {
      const passwordValidation = validatePasswordStrength(userData.password);
      if (!passwordValidation.isValid) {
        validationErrors.push({
          field: 'password',
          errorCode: passwordValidation.errorCode,
          errorMessage: passwordValidation.errorMessage
        });
      }
    }
  }

  // nameのバリデーション（設定されている場合のみ）
  if (userData.name !== undefined && userData.name !== null) {
    if (!userData.name || (typeof userData.name === 'string' && userData.name.trim().length === 0)) {
      validationErrors.push({
        field: 'name',
        errorCode: 'E004-0001',
        errorMessage: 'パラメータが不正です（name）'
      });
    } else {
      const nameValidation = validateName(userData.name);
      if (!nameValidation.isValid) {
        validationErrors.push({
          field: 'name',
          errorCode: nameValidation.errorCode,
          errorMessage: nameValidation.errorMessage
        });
      }
    }
  }

  // userKbnのバリデーション（設定されている場合のみ）
  let userKbnValidation = { isValid: true };
  if (userData.userKbn !== undefined && userData.userKbn !== null) {
    userKbnValidation = validateUserKbn(userData.userKbn);
    if (!userKbnValidation.isValid) {
      validationErrors.push({
        field: 'userKbn',
        errorCode: userKbnValidation.errorCode,
        errorMessage: userKbnValidation.errorMessage
      });
      userKbnValidation.isValid = false;
    }
  }

  // mail02のバリデーション（設定されている場合のみ）
  if (userData.mail02 !== undefined && userData.mail02 !== null) {
    const mail02Validation = validateEmail(userData.mail02);
    if (!mail02Validation.isValid) {
      validationErrors.push({
        field: 'mail02',
        errorCode: mail02Validation.errorCode,
        errorMessage: mail02Validation.errorMessage
      });
    }
  }

  // hallsのバリデーション（userKbnが有効な場合のみ実行）
  let hallsValidation = { isValid: true, normalizedArray: null };
  if (userKbnValidation.isValid && userData.userKbn !== undefined && userData.userKbn !== null) {
    hallsValidation = validateHalls(userData.halls, userData.userKbn);
    if (!hallsValidation.isValid) {
      validationErrors.push({
        field: 'halls',
        errorCode: hallsValidation.errorCode,
        errorMessage: hallsValidation.errorMessage
      });
    }
  } else if (userData.halls !== undefined && userData.halls !== null) {
    // userKbnが設定されていない場合でも、hallsが設定されている場合は配列に変換
    const hallsArray = Array.isArray(userData.halls) ? userData.halls : [userData.halls];
    hallsValidation.normalizedArray = hallsArray;
  }

  // transport_codeのバリデーション（userKbnが有効な場合のみ実行）
  let transportCodeValidation = { isValid: true };
  if (userKbnValidation.isValid && userData.userKbn !== undefined && userData.userKbn !== null) {
    transportCodeValidation = validateTransportCode(userData.transport_code, userData.userKbn);
    if (!transportCodeValidation.isValid) {
      validationErrors.push({
        field: 'transport_code',
        errorCode: transportCodeValidation.errorCode,
        errorMessage: transportCodeValidation.errorMessage
      });
    }
  } else if (userData.transport_code !== undefined && userData.transport_code !== null) {
    // userKbnが設定されていない場合でも、transport_codeが設定されている場合は形式チェック
    if (typeof userData.transport_code !== 'string' || userData.transport_code.trim().length === 0) {
      validationErrors.push({
        field: 'transport_code',
        errorCode: 'E004-0001',
        errorMessage: 'パラメータが不正です（transport_code）'
      });
    }
  }

  // transport_manage_flgのバリデーション（設定されている場合のみ）
  if (userData.transport_manage_flg !== undefined && userData.transport_manage_flg !== null) {
    if (!isValidBoolean(userData.transport_manage_flg)) {
      validationErrors.push({
        field: 'transport_manage_flg',
        errorCode: 'E004-0001',
        errorMessage: 'パラメータが不正です（transport_manage_flg）'
      });
    }
  }

  // manage_flgのバリデーション（設定されている場合のみ）
  if (userData.manage_flg !== undefined && userData.manage_flg !== null) {
    if (!isValidBoolean(userData.manage_flg)) {
      validationErrors.push({
        field: 'manage_flg',
        errorCode: 'E004-0001',
        errorMessage: 'パラメータが不正です（manage_flg）'
      });
    }
  }

  // バリデーションエラーがある場合はエラーをthrow
  if (validationErrors.length > 0) {
    const firstError = validationErrors[0];
    const error = new Error(`${firstError.errorCode}: ${firstError.errorMessage}`);
    error.code = firstError.errorCode;
    error.statusCode = 400; // バリデーションエラーはすべて400
    error.details = validationErrors;
    
    logError('ユーザー更新処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      http: {
        method: 'PUT',
        path: `/api/users/${originalUserIdNum}`,
        status: error.statusCode
      },
      data: {
        message_id: firstError.errorCode,
        validation_errors: validationErrors
      }
    });
    
    throw error;
  }

  // リクエストボディの準備
  const requestBody = {};
  
  // オプションパラメータの追加
  if (userData.password !== undefined && userData.password !== null) {
    requestBody.password = userData.password;
  }
  if (userData.name !== undefined && userData.name !== null) {
    requestBody.name = userData.name;
  }
  if (userData.userKbn !== undefined && userData.userKbn !== null) {
    requestBody.userKbn = userData.userKbn;
  }
  if (userData.roles !== undefined && userData.roles !== null) {
    requestBody.roles = userData.roles;
  }
  if (userData.manage_flg !== undefined && userData.manage_flg !== null) {
    requestBody.manage_flg = userData.manage_flg;
  }
  if (userData.mail02 !== undefined && userData.mail02 !== null) {
    requestBody.mail02 = userData.mail02;
  }
  if (userData.sys_client_code !== undefined && userData.sys_client_code !== null) {
    requestBody.sys_client_code = userData.sys_client_code;
  }
  if (userData.transport_manage_flg !== undefined && userData.transport_manage_flg !== null) {
    requestBody.transport_manage_flg = userData.transport_manage_flg;
  }
  if (hallsValidation.normalizedArray !== null) {
    requestBody.halls = hallsValidation.normalizedArray;
  }
  if (userData.transport_code !== undefined && userData.transport_code !== null) {
    requestBody.transport_code = userData.transport_code;
  }
  if (userData.mail_setting !== undefined && userData.mail_setting !== null) {
    requestBody.mail_setting = userData.mail_setting;
  }
  if (userData.payer !== undefined && userData.payer !== null) {
    requestBody.payer = userData.payer;
  }
  
  // 3. ユーザー更新API（PUT /api/users/{originalUserId}）を呼び出し（リトライ処理付き）
  const apiUrl = `${apiGatewayUrl}/api/users/${originalUserIdNum}`;
  const maxRetries = 3;
  let lastError = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    let response;
    try {
      response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // HttpOnly Cookieを送信するために必要
        body: JSON.stringify(requestBody)
      });
    } catch (fetchError) {
      // ネットワークエラーの場合、リトライ可能
      lastError = fetchError;
      
      if (attempt < maxRetries) {
        // リトライ前に待機（指数バックオフ）
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
        continue;
      }
      
      // 最大リトライ回数に達した場合
      const networkError = new Error('E006-0004: ネットワークエラーが発生しました');
      networkError.code = 'E006-0004';
      networkError.statusCode = 504;
      networkError.originalError = fetchError;
      
      logError('ユーザー更新処理でネットワークエラーが発生しました', {
        event: 'error',
        category: 'network',
        http: {
          method: 'PUT',
          path: `/api/users/${originalUserIdNum}`,
          status: 504
        },
        data: {
          message_id: 'E006-0004',
          retry_attempt: attempt
        }
      });
      
      throw networkError;
    }
    
    // レスポンスの解析
    let responseData;
    try {
      responseData = await response.json();
    } catch (parseError) {
      // レスポンス解析エラーはリトライ対象外（JSON形式が不正な場合はリトライしても解決しない）
      const error = new Error('E005-0004: ユーザー更新APIの取得エラー等');
      error.code = 'E005-0004';
      error.statusCode = 500;
      error.originalError = parseError;
      
      logError('ユーザー更新処理でレスポンス解析エラーが発生しました', {
        event: 'error',
        category: 'api',
        http: {
          method: 'PUT',
          path: `/api/users/${originalUserIdNum}`,
          status: 500
        },
        data: {
          message_id: 'E005-0004'
        }
      });
      
      throw error;
    }
    
    // エラーレスポンスの処理
    if (!response.ok) {
      // 5xxエラー（サーバーエラー）はリトライ可能（429レート制限エラーを除く）
      const isRetryableError = response.status >= 500 && response.status !== 429;
      
      if (isRetryableError && attempt < maxRetries) {
        // リトライ前に待機（指数バックオフ）
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
        continue;
      }
      
      const apiError = handleApiError(response, responseData, 'ユーザー更新APIの取得エラー等');
      
      // エラーログ出力
      logError('ユーザー更新処理でAPIエラーが発生しました', {
        event: 'error',
        category: 'api',
        http: {
          method: 'PUT',
          path: `/api/users/${originalUserIdNum}`,
          status: apiError.statusCode
        },
        data: {
          message_id: apiError.code || 'E005-0004',
          retry_attempt: attempt
        }
      });
      
      throw apiError;
    }
    
    // 4. レスポンスからdataを取得
    if (!responseData.data) {
      const error = new Error('E005-0004: ユーザー更新APIの取得エラー等');
      error.code = 'E005-0004';
      error.statusCode = 500;
      
      logError('ユーザー更新処理でレスポンスデータが不正です', {
        event: 'error',
        category: 'api',
        http: {
          method: 'PUT',
          path: `/api/users/${originalUserIdNum}`,
          status: 500
        },
        data: {
          message_id: 'E005-0004'
        }
      });
      
      throw error;
    }
    
    const userInfo = responseData.data;
    
    // 5. 更新結果を返却
    // アクセスログ出力
    info('ユーザー更新処理が正常に完了しました', {
      event: 'user_update_success',
      category: 'user',
      http: {
        method: 'PUT',
        path: `/api/users/${originalUserIdNum}`,
        status: 200
      },
      data: {
        originalUserId: userInfo.originalUserId,
        email: userInfo.email,
        userKbn: userInfo.userKbn
      }
    });
    
    return userInfo;
  }
  
  // ここには到達しないはずだが、念のため
  throw lastError || new Error('E000-0001: システムエラーが発生しました');
}

/**
 * ユーザー削除
 * ユーザー削除API（DELETE /api/users/{originalUserId}）を呼び出す
 * 
 * 設計ドキュメント: 06_17_ユーザー削除処理詳細設計.md
 * 
 * @param {number} originalUserId - 777EC DBのユーザーID（必須、0以上の整数）
 * @param {Object} [options] - 追加オプション（任意）
 * @param {boolean} [options.force] - 強制削除フラグ（任意、デフォルト: false）
 * @returns {Promise<DeleteResult>} 削除結果
 * @throws {Error} バリデーションエラーまたはAPI呼び出しエラー
 * 
 * @typedef {Object} DeleteResult
 * @property {number} originalUserId - 777EC DBのユーザーID
 * @property {string} deletedAt - 削除日時
 */
async function deleteUser(originalUserId, options = {}) {
  // ブラウザ環境チェック
  if (typeof window === 'undefined') {
    const error = new Error('E005-0003: ブラウザ環境で実行する必要があります');
    error.code = 'E005-0003';
    error.statusCode = 500;
    
    logError('ユーザー削除処理で環境エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'DELETE',
        path: `/api/users/${originalUserId}`,
        status: 500
      },
      data: {
        message_id: 'E005-0003'
      }
    });
    
    throw error;
  }

  // API Gateway URLの取得
  const apiGatewayUrl = API_GATEWAY_URL;
  if (!apiGatewayUrl || typeof apiGatewayUrl !== 'string' || apiGatewayUrl.trim().length === 0) {
    const error = new Error('E005-0003: 環境変数が設定されていない');
    error.code = 'E005-0003';
    error.statusCode = 500;
    
    logError('ユーザー削除処理で環境変数エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'DELETE',
        path: `/api/users/${originalUserId}`,
        status: 500
      },
      data: {
        message_id: 'E005-0003'
      }
    });
    
    throw error;
  }

  // 1. originalUserIdのバリデーション
  if (originalUserId === undefined || originalUserId === null) {
    const error = new Error('E004-0002: 必須パラメータが不足しています（originalUserId）');
    error.code = 'E004-0002';
    error.statusCode = 400;
    
    logError('ユーザー削除処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      http: {
        method: 'DELETE',
        path: '/api/users/{userId}',
        status: 400
      },
      data: {
        message_id: 'E004-0002',
        field: 'originalUserId'
      }
    });
    
    throw error;
  }

  const originalUserIdNum = typeof originalUserId === 'number' 
    ? originalUserId 
    : parseInt(originalUserId, 10);
  
  if (isNaN(originalUserIdNum) || originalUserIdNum < 0) {
    const error = new Error('E004-0001: パラメータが不正です（originalUserId）');
    error.code = 'E004-0001';
    error.statusCode = 400;
    
    logError('ユーザー削除処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      http: {
        method: 'DELETE',
        path: `/api/users/${originalUserIdNum}`,
        status: 400
      },
      data: {
        message_id: 'E004-0001',
        field: 'originalUserId',
        originalUserId: originalUserId
      }
    });
    
    throw error;
  }

  // 2. options.forceのバリデーション
  if (options !== null && typeof options !== 'object') {
    const error = new Error('E004-0001: パラメータが不正です（options）');
    error.code = 'E004-0001';
    error.statusCode = 400;
    
    logError('ユーザー削除処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      http: {
        method: 'DELETE',
        path: `/api/users/${originalUserIdNum}`,
        status: 400
      },
      data: {
        message_id: 'E004-0001',
        field: 'options'
      }
    });
    
    throw error;
  }

  let force = false;
  let forceProvided = false;
  if (options && options.force !== undefined && options.force !== null) {
    if (typeof options.force !== 'boolean') {
      const error = new Error('E004-0001: パラメータが不正です（force）');
      error.code = 'E004-0001';
      error.statusCode = 400;
      
      logError('ユーザー削除処理でバリデーションエラーが発生しました', {
        event: 'error',
        category: 'validation',
        http: {
          method: 'DELETE',
          path: `/api/users/${originalUserIdNum}`,
          status: 400
        },
        data: {
          message_id: 'E004-0001',
          field: 'force'
        }
      });
      
      throw error;
    }
    
    force = options.force;
    forceProvided = true;
  }

  // 3. ユーザー削除API（DELETE /api/users/{originalUserId}）を呼び出し（リトライ処理付き）
  // クエリパラメータにforceを設定（設定されている場合のみ）
  let apiUrl = `${apiGatewayUrl}/api/users/${originalUserIdNum}`;
  if (forceProvided) {
    apiUrl += `?force=${force}`;
  }
  
  const maxRetries = 3;
  let lastError = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    let response;
    try {
      response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include' // HttpOnly Cookieを送信するために必要
      });
    } catch (fetchError) {
      // ネットワークエラーの場合、リトライ可能
      lastError = fetchError;
      
      if (attempt < maxRetries) {
        // リトライ前に待機（指数バックオフ）
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
        continue;
      }
      
      // 最大リトライ回数に達した場合
      const networkError = new Error('E006-0004: ネットワークエラーが発生しました');
      networkError.code = 'E006-0004';
      networkError.statusCode = 504;
      networkError.originalError = fetchError;
      
      logError('ユーザー削除処理でネットワークエラーが発生しました', {
        event: 'error',
        category: 'network',
        http: {
          method: 'DELETE',
          path: `/api/users/${originalUserIdNum}`,
          status: 504
        },
        data: {
          message_id: 'E006-0004',
          retry_attempt: attempt
        }
      });
      
      throw networkError;
    }
    
    // レスポンスの解析
    let responseData;
    try {
      responseData = await response.json();
    } catch (parseError) {
      // レスポンス解析エラーはリトライ対象外（JSON形式が不正な場合はリトライしても解決しない）
      const error = new Error('E005-0004: ユーザー削除APIの取得エラー等');
      error.code = 'E005-0004';
      error.statusCode = 500;
      error.originalError = parseError;
      
      logError('ユーザー削除処理でレスポンス解析エラーが発生しました', {
        event: 'error',
        category: 'api',
        http: {
          method: 'DELETE',
          path: `/api/users/${originalUserIdNum}`,
          status: 500
        },
        data: {
          message_id: 'E005-0004'
        }
      });
      
      throw error;
    }
    
    // エラーレスポンスの処理
    if (!response.ok) {
      // 5xxエラー（サーバーエラー）はリトライ可能（429レート制限エラーを除く）
      const isRetryableError = response.status >= 500 && response.status !== 429;
      
      if (isRetryableError && attempt < maxRetries) {
        // リトライ前に待機（指数バックオフ）
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
        continue;
      }
      
      const apiError = handleApiError(response, responseData, 'ユーザー削除APIの取得エラー等');
      
      // エラーログ出力
      logError('ユーザー削除処理でAPIエラーが発生しました', {
        event: 'error',
        category: 'api',
        http: {
          method: 'DELETE',
          path: `/api/users/${originalUserIdNum}`,
          status: apiError.statusCode
        },
        data: {
          message_id: apiError.code || 'E005-0004',
          retry_attempt: attempt
        }
      });
      
      throw apiError;
    }
    
    // 4. レスポンスからdataを取得
    if (!responseData.data) {
      const error = new Error('E005-0004: ユーザー削除APIの取得エラー等');
      error.code = 'E005-0004';
      error.statusCode = 500;
      
      logError('ユーザー削除処理でレスポンスデータが不正です', {
        event: 'error',
        category: 'api',
        http: {
          method: 'DELETE',
          path: `/api/users/${originalUserIdNum}`,
          status: 500
        },
        data: {
          message_id: 'E005-0004'
        }
      });
      
      throw error;
    }
    
    const deleteResult = responseData.data;
    
    // 5. 削除結果を返却
    // DeleteResult型に変換（originalUserIdとdeletedAtを返却）
    const result = {
      originalUserId: deleteResult.originalUserId,
      deletedAt: deleteResult.deletedAt
    };
    
    // アクセスログ出力
    info('ユーザー削除処理が正常に完了しました', {
      event: 'user_delete_success',
      category: 'user',
      http: {
        method: 'DELETE',
        path: `/api/users/${originalUserIdNum}`,
        status: 200
      },
      data: {
        originalUserId: result.originalUserId,
        deletedAt: result.deletedAt
      }
    });
    
    return result;
  }
  
  // ここには到達しないはずだが、念のため
  throw lastError || new Error('E000-0001: システムエラーが発生しました');
}

module.exports = {
  // ユーザー登録
  registerUser,
  // 後方互換性のためcreateUserもエクスポート
  createUser: registerUser,
  
  // ユーザー更新
  updateUser,
  
  // ユーザー削除
  deleteUser,
  
  // ユーザー情報取得
  getUserInfo,
  
  // ユーザー検索
  searchUsers: (searchParams) => {
    throw new Error('Not implemented');
  }
};
